import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { squatApi } from '@/lib/api';
import type { SquatProgram, SquatSession, SquatSet } from '@/types';

// Aktiivinen ohjelma (sessioineen)
export function useActiveSquatProgram() {
  return useQuery({
    queryKey: ['squat', 'active'],
    queryFn: () => squatApi.getActive().then((r) => r.program),
  });
}

// Ohjelman staattinen määrittely (ei muutu)
export function useSquatDefinition() {
  return useQuery({
    queryKey: ['squat', 'definition'],
    queryFn: () => squatApi.getDefinition().then((r) => r.weeks),
    staleTime: Infinity,
  });
}

// Yksittäinen session
export function useSquatSession(sessionId: string | undefined) {
  return useQuery({
    queryKey: ['squat', 'session', sessionId],
    queryFn: () => squatApi.getSession(sessionId!).then((r) => r.session),
    enabled: !!sessionId,
  });
}

// Ohjelman tilastot
export function useSquatStats(programId: string | undefined) {
  return useQuery({
    queryKey: ['squat', 'stats', programId],
    queryFn: () => squatApi.getStats(programId!),
    enabled: !!programId,
  });
}

// Luo uusi ohjelma
export function useCreateSquatProgram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { oneRepMax: number; notes?: string }) =>
      squatApi.createProgram(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['squat'] });
    },
  });
}

// Aloita (tai jatka) viikon treeni
export function useStartSquatSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ programId, weekNumber }: { programId: string; weekNumber: number }) =>
      squatApi.startSession(programId, weekNumber),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['squat', 'active'] });
    },
  });
}

// Päivitä sarja optimistisesti
export function useUpdateSquatSet(sessionId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ setId, data }: { setId: string; data: { actualReps: number; completed: boolean } }) =>
      squatApi.updateSet(setId, data),
    onMutate: async ({ setId, data }) => {
      if (!sessionId) return;
      await qc.cancelQueries({ queryKey: ['squat', 'session', sessionId] });

      const previous = qc.getQueryData<SquatSession>(['squat', 'session', sessionId]);
      if (!previous) return { previous };

      qc.setQueryData<SquatSession>(['squat', 'session', sessionId], {
        ...previous,
        sets: previous.sets.map((s: SquatSet) =>
          s.id === setId
            ? {
                ...s,
                actualReps: data.actualReps,
                completed: data.completed,
                completedAt: data.completed ? new Date().toISOString() : null,
              }
            : s
        ),
      });

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous && sessionId) {
        qc.setQueryData(['squat', 'session', sessionId], context.previous);
      }
    },
    onSettled: () => {
      if (sessionId) {
        qc.invalidateQueries({ queryKey: ['squat', 'session', sessionId] });
      }
      qc.invalidateQueries({ queryKey: ['squat', 'active'] });
    },
  });
}

// Päätä session (+ PR-tarkistus viikko 7)
export function useCompleteSquatSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, data }: { sessionId: string; data: { rpe?: number; notes?: string } }) =>
      squatApi.completeSession(sessionId, data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['squat', 'session', vars.sessionId] });
      qc.invalidateQueries({ queryKey: ['squat', 'active'] });
      qc.invalidateQueries({ queryKey: ['squat', 'stats'] });
    },
  });
}

// Hylkää aktiivinen ohjelma
export function useAbandonSquatProgram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (programId: string) => squatApi.abandonProgram(programId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['squat'] });
    },
  });
}

// Poista keskeneräinen session
export function useDeleteSquatSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => squatApi.deleteSession(sessionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['squat'] });
    },
  });
}

// Apuri: palauta numero myös stringistä (Prisma Decimal)
export function toNumber(v: string | number | null | undefined): number {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return v;
  return Number(v);
}

// Apuri: luokittele session tila
export type SessionStatus = 'not_started' | 'in_progress' | 'completed';

export function getWeekStatus(
  weekNumber: number,
  program: SquatProgram | null | undefined
): { status: SessionStatus; session?: SquatSession } {
  if (!program?.sessions) return { status: 'not_started' };
  const session = program.sessions.find((s) => s.weekNumber === weekNumber);
  if (!session) return { status: 'not_started' };
  if (session.isCompleted) return { status: 'completed', session };
  return { status: 'in_progress', session };
}
